export function useFacilityImportGuard() {
  const { t } = useFormatOptions();
  const appStore = useAppStore();
  const {
    isLoadingImportData,
    importPagesList,
    activeImportFacilityId,
    loadAllImportedData,
    hasPageDraftSnapshot,
  } = useImportFacilityApi();
  const { load: loadImportPage } = useImportPage();
  const bulkImportStore = useBulkImportStore();
  const importFacilityStore = useImportFacilityStore();
  const { activeFacilityId, currentFacility, isLoadingCurrentFacility } =
    useFacilityApi();
  const { makeGuardResult, resolveFacilityRouteContext, createFacilityPath } =
    useFacilityRoute();

  const guardBulkImportRoute = (
    routeContext: ReturnType<typeof resolveFacilityRouteContext>,
  ): GuardResult => {
    if (!routeContext.matches.bulkImportFlow)
      return makeGuardResult(routeContext.route, "continue");

    if (isLoadingImportData.value) {
      devConsole.info(
        "🚦 guard rule triggered: Waiting for current import data to load",
      );
      return makeGuardResult(routeContext.route, "continue");
    }

    if (
      routeContext.matches.bulkImportFlow &&
      routeContext.params.pageId &&
      (!bulkImportStore.hasCurrentPageSetup ||
        bulkImportStore.currentPageSetupId !== routeContext.params.pageId)
    ) {
      devConsole.info(
        "🚦 guard rule triggered: initialize currentPageSetup for bulk import",
      );
      bulkImportStore.init(routeContext.params.pageId);
      loadImportPage();
    }

    /**
     * Prevent navigation to uploaded column mapping without pending columns mapping
     */
    if (routeContext.matches.mapColumns && !bulkImportStore.isMappingPending) {
      devConsole.error(
        "🚦 guard rule triggered: Prevent navigation to uploaded column mapping without pending columns",
      );

      return makeGuardResult(routeContext.route, {
        redirect: createFacilityPath({
          import: true,
          facilityId: routeContext.params.facilityId!,
          pageId: routeContext.params.pageId,
        }),
      });
    }

    /**
     * Prevent navigation to merge conflicts without any merge conflicts
     */
    if (
      routeContext.matches.mergeConflicts &&
      !bulkImportStore.hasMergeConflicts
    ) {
      devConsole.error(
        "🚦 guard rule triggered: Prevent navigation to merge conflicts without any merge conflicts",
        routeContext,
      );
      return makeGuardResult(routeContext.route, {
        redirect: createFacilityPath({
          import: true,
          facilityId: routeContext.params.facilityId!,
          pageId: routeContext.params.pageId,
        }),
      });
    }

    const currentTask = importPagesList.value.find(
      (task) => task.id === routeContext.params.pageId,
    );

    if (currentTask?.taskLocked && !routeContext.matches.learnMore) {
      devConsole.error(
        "🚦 guard rule triggered: Prevent navigation to locked task",
      );

      appStore.addWarningMessage(t("cksetup.imports.bulk.lockedTaskMessage"));

      return makeGuardResult(routeContext.route, {
        redirect: createFacilityPath({
          import: true,
          facilityId: routeContext.params.facilityId!,
        }),
      });
    }

    return makeGuardResult(routeContext.route, "continue");
  };

  const guardFeatureRoute: FeatureGuard = async (route) => {
    const routeContext = resolveFacilityRouteContext(route);

    if (!routeContext.isRecognized) {
      devConsole.error(
        `🚦 guard rule triggered: unrecognized route name for ${route.path}`,
        routeContext,
      );
      return makeGuardResult(route, "root");
    }

    if (!routeContext.matches.importFlow)
      return makeGuardResult(route, "continue");

    if (!routeContext.params.facilityId || !activeFacilityId.value) {
      devConsole.error(
        "🚦 guard rule triggered: Prevent navigation to any import page without activeFacilityId set",
      );
      return makeGuardResult(route, "root");
    }

    if (isLoadingCurrentFacility.value || !currentFacility.value) {
      devConsole.info(
        "🚦 guard rule triggered: Waiting for current facility to load",
      );
      return makeGuardResult(route, "continue");
    }

    if (routeContext.params.facilityId !== activeImportFacilityId.value) {
      devConsole.info(
        "🚦 guard rule triggered: load all imported data for a new facility",
      );

      await loadAllImportedData();
    }

    return guardBulkImportRoute(routeContext);
  };

  /**
   * Guard the route when leaving the facility import flow
   * @param from
   * @param to
   * @returns false if the user should be prompted to confirm unsaved changes
   */
  const guardExitRoute: ExitGuard = ({ from, to }) => {
    const fromRouteContext = resolveFacilityRouteContext(from);
    const toRouteContext = resolveFacilityRouteContext(to);

    // Cleanup (leaving routes) case scenarios
    if (!fromRouteContext.isRecognized) return makeGuardResult(to, "continue");
    const switchedFacilityId = from.params.facilityId !== to.params.facilityId;
    const switchedBulkImportPageId =
      fromRouteContext.params.pageId !== toRouteContext.params.pageId;

    const isLeavingBulkImport =
      fromRouteContext.matches.bulkImportFlow &&
      (!toRouteContext.matches.bulkImportFlow ||
        (!fromRouteContext.matches.uploadFile &&
          toRouteContext.matches.uploadFile) ||
        switchedFacilityId ||
        switchedBulkImportPageId);

    const isLeavingImportPage =
      fromRouteContext.matches.importFlow &&
      (!toRouteContext.matches.importFlow || switchedFacilityId);

    if (isLeavingBulkImport) {
      if (
        !hasPageDraftSnapshot(bulkImportStore.currentPageSetupId) &&
        bulkImportStore.confirmUnsavedChangesBeforeLeaving(
          toRouteContext.localizedPath,
        )
      ) {
        devConsole.warn(
          "🚦 middleware rule triggered: there are unsaved changes (but not in memory), showing a confirmation dialog",
        );
        return makeGuardResult(to, "abort");
      }

      devConsole.info(
        "🚦 middleware rule triggered: resetting bulk import store as user is leaving the bulk import page",
      );
      bulkImportStore.reset();
    }

    if (isLeavingImportPage) {
      devConsole.info(
        "🚦 middleware rule triggered: Resetting import facility store as user is going out of facility import page",
      );
      importFacilityStore.reset();
    }

    return makeGuardResult(to, "continue");
  };

  return {
    name: "facilityImportGuard",
    guardFeatureRoute,
    guardExitRoute,
  };
}
